import { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { updateGameNotificationPreferences, addDismissedGame } from "@/redux/meta/metaActions";
import { GameNotificationPreferences } from "@/redux/meta/InitialMetaStore";

const STORAGE_KEY = "devourplay_game_notification_preferences";

/**
 * Hook to manage game notification preferences with localStorage persistence
 */
export const useGameNotificationPreferences = () => {
    const dispatch = useDispatch();
    const preferences = useSelector((store: IStore) => store.metaStore.gameNotificationPreferences);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);

    // Load preferences from localStorage on mount
    useEffect(() => {
        try {
            const savedPreferences = localStorage.getItem(STORAGE_KEY);
            if (savedPreferences) {
                const parsed: Partial<GameNotificationPreferences> = JSON.parse(savedPreferences);
                // Merge with current preferences to ensure all fields exist
                dispatch(updateGameNotificationPreferences({
                    showGameDetectedNotifications: parsed.showGameDetectedNotifications ?? true,
                    showGameExitNotifications: parsed.showGameExitNotifications ?? true,
                    autoEnterGameMode: parsed.autoEnterGameMode ?? false,
                    dismissedGames: parsed.dismissedGames ?? [],
                }));
            }
        } catch (error) {
            console.warn("Failed to load game notification preferences from localStorage:", error);
        }
    }, [dispatch]);

    // Save preferences to localStorage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
        } catch (error) {
            console.warn("Failed to save game notification preferences to localStorage:", error);
        }
    }, [preferences]);

    // Helper functions for updating preferences
    const updatePreferences = useCallback((updates: Partial<GameNotificationPreferences>) => {
        dispatch(updateGameNotificationPreferences(updates));
    }, [dispatch]);

    const toggleGameDetectedNotifications = useCallback(() => {
        updatePreferences({ showGameDetectedNotifications: !preferences.showGameDetectedNotifications });
    }, [updatePreferences, preferences.showGameDetectedNotifications]);

    const toggleGameExitNotifications = useCallback(() => {
        updatePreferences({ showGameExitNotifications: !preferences.showGameExitNotifications });
    }, [updatePreferences, preferences.showGameExitNotifications]);

    const toggleAutoEnterGameMode = useCallback(() => {
        updatePreferences({ autoEnterGameMode: !preferences.autoEnterGameMode });
    }, [updatePreferences, preferences.autoEnterGameMode]);

    const dismissGameNotifications = useCallback((gameId: string) => {
        dispatch(addDismissedGame(gameId));
    }, [dispatch]);

    const undismissGameNotifications = useCallback((gameId: string) => {
        const updatedDismissedGames = preferences.dismissedGames.filter(id => id !== gameId);
        updatePreferences({ dismissedGames: updatedDismissedGames });
    }, [updatePreferences, preferences.dismissedGames]);

    const isGameDismissed = (gameId: string): boolean => {
        return preferences.dismissedGames.includes(gameId);
    };

    // Check if notifications should be shown based on preferences and context
    const shouldShowGameDetectedNotification = useCallback((gameId: string): boolean => {
        return isOnOverwolf &&
               preferences.showGameDetectedNotifications &&
               !isGameDismissed(gameId);
    }, [isOnOverwolf, preferences.showGameDetectedNotifications, preferences.dismissedGames]);

    const shouldShowGameExitNotification = useCallback((gameId: string): boolean => {
        return isOnOverwolf &&
               preferences.showGameExitNotifications &&
               !isGameDismissed(gameId);
    }, [isOnOverwolf, preferences.showGameExitNotifications, preferences.dismissedGames]);

    const shouldAutoEnterGameMode = useCallback((gameId: string): boolean => {
        return isOnOverwolf &&
               preferences.autoEnterGameMode &&
               !isGameDismissed(gameId);
    }, [isOnOverwolf, preferences.autoEnterGameMode, preferences.dismissedGames]);

    return {
        preferences,
        updatePreferences,
        toggleGameDetectedNotifications,
        toggleGameExitNotifications,
        toggleAutoEnterGameMode,
        dismissGameNotifications,
        undismissGameNotifications,
        isGameDismissed,
        shouldShowGameDetectedNotification,
        shouldShowGameExitNotification,
        shouldAutoEnterGameMode,
    };
};